import {
  Accordion,
  AskStrikeUpButton,
  Badge,
  Box,
  Button,
  Flex,
  Heading,
  HStack,
  Text,
  VStack,
} from "components/ui";
import React, { useEffect, useState } from "react";
import { FaSave, FaUndo } from "react-icons/fa";
import { Checkbox } from "../../../../components/ui/checkbox";
import { Select } from "../../../../components/ui/select";
import { useTranslation } from "../../../../hooks/useTranslation";
import {
  FeatureFlag,
  featureFlagService,
  ToolMapping,
} from "../../../../services/featureFlagService";
import { showToast } from "../../../../utils/toast";

interface ServerToolSelection {
  [serverId: string]: Set<string>; // Set of selected tool IDs per server
}

const ToolMappingsPage: React.FC = () => {
  const { t } = useTranslation();
  const [flags, setFlags] = useState<FeatureFlag[]>([]);
  const [selectedFlagKey, setSelectedFlagKey] = useState<string>("");
  const [toolMappings, setToolMappings] = useState<ToolMapping[]>([]);
  const [selections, setSelections] = useState<ServerToolSelection>({});
  const [initialSelections, setInitialSelections] =
    useState<ServerToolSelection>({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Load active flags
  useEffect(() => {
    const loadFlags = async () => {
      try {
        const data = await featureFlagService.getActiveFlags();
        setFlags(data);
      } catch (error: any) {
        showToast({
          title: t("app.featureFlags.mappings.errorLoadingFlags"),
          description: error.message,
          status: "error",
        });
      }
    };
    loadFlags();
  }, []);

  // Load tool mappings when flag is selected
  useEffect(() => {
    if (!selectedFlagKey) {
      setToolMappings([]);
      setSelections({});
      setInitialSelections({});
      return;
    }

    const loadMappings = async () => {
      try {
        setLoading(true);
        const mappings =
          await featureFlagService.getMappingsByFlag(selectedFlagKey);
        setToolMappings(mappings);

        // Build initial selections
        const newSelections: ServerToolSelection = {};
        mappings.forEach((mapping) => {
          newSelections[mapping.serverId] = new Set(
            mapping.tools.map((t) => t.toolId),
          );
        });
        setSelections(newSelections);
        setInitialSelections(
          JSON.parse(
            JSON.stringify(
              Object.fromEntries(
                Object.entries(newSelections).map(([k, v]) => [
                  k,
                  Array.from(v),
                ]),
              ),
            ),
          ),
        );
      } catch (error: any) {
        showToast({
          title: t("app.featureFlags.mappings.errorLoadingFlags"),
          description: error.message,
          status: "error",
        });
      } finally {
        setLoading(false);
      }
    };

    loadMappings();
  }, [selectedFlagKey]);

  const handleToolToggle = (
    serverId: string,
    toolId: string,
    checked: boolean,
  ) => {
    setSelections((prev) => {
      const newSelections = { ...prev };
      if (!newSelections[serverId]) {
        newSelections[serverId] = new Set();
      }

      if (checked) {
        newSelections[serverId].add(toolId);
      } else {
        newSelections[serverId].delete(toolId);
      }

      return newSelections;
    });
  };

  const handleSelectAll = (serverId: string) => {
    const mapping = toolMappings.find((m) => m.serverId === serverId);
    if (!mapping) return;

    setSelections((prev) => ({
      ...prev,
      [serverId]: new Set(mapping.tools.map((t) => t.toolId)),
    }));
  };

  const handleDeselectAll = (serverId: string) => {
    setSelections((prev) => ({
      ...prev,
      [serverId]: new Set(),
    }));
  };

  const handleReset = () => {
    // Convert initial selections back to Sets
    const resetSelections: ServerToolSelection = {};
    Object.entries(initialSelections).forEach(([serverId, toolIds]) => {
      resetSelections[serverId] = new Set(toolIds as unknown as string[]);
    });
    setSelections(resetSelections);
  };

  const handleSave = async () => {
    if (!selectedFlagKey) return;

    try {
      setSaving(true);

      // Calculate changes for each server
      for (const mapping of toolMappings) {
        const { serverId } = mapping;
        const currentSelection = selections[serverId] || new Set();
        const initialSelection = new Set(initialSelections[serverId] || []);

        // Tools to add
        const toAdd = Array.from(currentSelection).filter(
          (id) => !initialSelection.has(id),
        );
        // Tools to remove
        const toRemove = Array.from(initialSelection).filter(
          (id) => !currentSelection.has(id),
        );

        // Make API calls
        if (toAdd.length > 0) {
          await featureFlagService.assignToolsToFlag(
            selectedFlagKey,
            serverId,
            toAdd,
          );
        }
        if (toRemove.length > 0) {
          await featureFlagService.removeToolsFromFlag(
            selectedFlagKey,
            serverId,
            toRemove,
          );
        }
      }

      showToast({
        title: t("app.featureFlags.mappings.mappingsSaved"),
        description: t("app.featureFlags.mappings.mappingsSavedDescription"),
        status: "success",
      });

      // Update initial selections to current
      setInitialSelections(
        JSON.parse(
          JSON.stringify(
            Object.fromEntries(
              Object.entries(selections).map(([k, v]) => [k, Array.from(v)]),
            ),
          ),
        ),
      );
    } catch (error: any) {
      showToast({
        title: t("app.featureFlags.mappings.errorSavingMappings"),
        description: error.response?.data?.message || error.message,
        status: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const hasChanges =
    JSON.stringify(
      Object.fromEntries(
        Object.entries(selections).map(([k, v]) => [k, Array.from(v)]),
      ),
    ) !== JSON.stringify(initialSelections);

  const selectedFlag = flags.find((f) => f.key === selectedFlagKey);

  return (
    <Box p={6}>
      <VStack align="stretch" gap={6}>
        {/* Header */}
        <Flex justify="space-between" align="center" wrap="wrap" gap={3}>
          <Box>
            <Heading size="lg">{t("app.featureFlags.mappings.title")}</Heading>
            <Text color="gray.600" mt={1}>
              {t("app.featureFlags.mappings.description")}
            </Text>
          </Box>
          <AskStrikeUpButton
            context={t("app.askStrikeUp.context.featureFlagsMappings")}
          />
        </Flex>

        {/* Flag Selector */}
        <Box>
          <Text fontWeight="medium" mb={2}>
            {t("app.featureFlags.mappings.selectPolicy")}
          </Text>
          <Select
            placeholder={t("app.featureFlags.mappings.choosePolicyPlaceholder")}
            items={flags.map((f) => ({
              value: f.key,
              label: `${f.name} (${f.key})`,
            }))}
            value={selectedFlagKey ? [selectedFlagKey] : []}
            onValueChange={(details: { value: string[] }) =>
              setSelectedFlagKey(details.value[0] || "")
            }
            width="400px"
          />
          {selectedFlag && (
            <Text fontSize="sm" color="gray.600" mt={2}>
              {selectedFlag.description ||
                t("app.featureFlags.mappings.noDescription")}
            </Text>
          )}
        </Box>

        {/* Tool Mappings */}
        {selectedFlagKey && (
          <>
            {loading ? (
              <Text>{t("app.featureFlags.mappings.loadingMappings")}</Text>
            ) : toolMappings.length === 0 ? (
              <Box p={6} borderRadius="md" textAlign="center">
                <Text color="gray.500">
                  {t("app.featureFlags.mappings.noServers")}
                </Text>
              </Box>
            ) : (
              <>
                {/* Action Buttons */}
                <HStack justify="space-between">
                  <Text fontSize="sm" color="gray.600">
                    {t("app.featureFlags.mappings.selectToolsDescription")}
                  </Text>
                  <HStack>
                    <Button
                      variant="outline"
                      onClick={handleReset}
                      disabled={!hasChanges}
                      size="sm"
                    >
                      <FaUndo /> {t("app.featureFlags.mappings.reset")}
                    </Button>
                    <Button
                      colorPalette="blue"
                      onClick={handleSave}
                      loading={saving}
                      disabled={!hasChanges}
                      size="sm"
                    >
                      <FaSave /> {t("app.featureFlags.mappings.saveChanges")}
                    </Button>
                  </HStack>
                </HStack>

                {/* Server Accordions */}
                <Accordion.Root
                  multiple
                  defaultValue={toolMappings.map((m) => m.serverId)}
                >
                  {toolMappings.map((mapping) => {
                    const selectedCount = (
                      selections[mapping.serverId] || new Set()
                    ).size;
                    const totalCount = mapping.tools.length;

                    return (
                      <Accordion.Item
                        key={mapping.serverId}
                        value={mapping.serverId}
                      >
                        <Accordion.ItemTrigger>
                          <HStack flex={1} justify="space-between">
                            <Text fontWeight="medium">
                              {mapping.serverName}
                            </Text>
                            <Badge
                              colorPalette={selectedCount > 0 ? "blue" : "gray"}
                            >
                              {t("app.featureFlags.mappings.toolsCount", {
                                selected: selectedCount,
                                total: totalCount,
                              })}
                            </Badge>
                          </HStack>
                        </Accordion.ItemTrigger>
                        <Accordion.ItemContent>
                          <VStack align="stretch" gap={3}>
                            {/* Bulk Actions */}
                            <HStack justify="flex-end">
                              <Button
                                size="xs"
                                variant="outline"
                                onClick={() =>
                                  handleSelectAll(mapping.serverId)
                                }
                              >
                                {t("app.featureFlags.mappings.selectAll")}
                              </Button>
                              <Button
                                size="xs"
                                variant="outline"
                                onClick={() =>
                                  handleDeselectAll(mapping.serverId)
                                }
                              >
                                {t("app.featureFlags.mappings.deselectAll")}
                              </Button>
                            </HStack>

                            {/* Tool Checkboxes */}
                            {mapping.tools.map((tool) => (
                              <Checkbox
                                key={tool.toolId}
                                checked={(
                                  selections[mapping.serverId] || new Set()
                                ).has(tool.toolId)}
                                onCheckedChange={(e: { checked: boolean }) =>
                                  handleToolToggle(
                                    mapping.serverId,
                                    tool.toolId,
                                    e.checked,
                                  )
                                }
                              >
                                <VStack align="start" gap={0}>
                                  <Text fontWeight="medium">
                                    {tool.toolName}
                                  </Text>
                                  {tool.toolDescription && (
                                    <Text fontSize="sm" color="gray.600">
                                      {tool.toolDescription}
                                    </Text>
                                  )}
                                </VStack>
                              </Checkbox>
                            ))}
                          </VStack>
                        </Accordion.ItemContent>
                      </Accordion.Item>
                    );
                  })}
                </Accordion.Root>
              </>
            )}
          </>
        )}
      </VStack>
    </Box>
  );
};

export default ToolMappingsPage;
